import Email from "@/domain/entities/emails/Email";
import Thread from "@/domain/entities/emails/Thread";
import useGetUserProfile from "@/hooks/useGetUserProfile";
import { useEffect, useMemo, useRef, useState } from "react";
import { ScrollArea } from "../ui/scroll-area";
import Spinner from "../ui/spinner";
import MailContentWithSenderAndDate from "./MailContentWithSenderAndDate";

type MailFeedProps = {
    mail?: Email;
    mailsThread?: Thread;
    isLoading: boolean;
    /** How the selected mail of the thread stands out (default: the purple border). */
    selectedClassName?: string;
};

const MailFeed = ({
    mail,
    mailsThread,
    isLoading,
    selectedClassName,
}: MailFeedProps) => {
    const { data: userProfile, isLoading: isUserProfileLoading } =
        useGetUserProfile();

    const mailRef = useRef<HTMLDivElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);
    const lastScrolledMailIdRef = useRef<string | null>(null);
    const [newMailCount, setNewMailCount] = useState<number>(
        mailsThread?.mails.length ?? 0,
    );

    // Filters out empty mails sent as a response to an event invitation
    const mailsThreadFiltered = useMemo(
        () =>
            mailsThread?.mails.filter(
                (mailItem) =>
                    !(
                        mailItem.message.trim() === "" &&
                        mailItem.linkedEventDetails
                    ),
            ),
        [mailsThread],
    );

    const targetMailId = mail?.id;
    const canScrollToTarget =
        !!targetMailId &&
        !isLoading &&
        !isUserProfileLoading &&
        !!userProfile &&
        !!mailsThreadFiltered?.some((m) => m.id === targetMailId);

    // Scrolls to the opened mail. The mails above it render inside iframes
    // whose height resolves asynchronously (see MailIframe), so a single
    // scroll fires too early; we re-align until the layout settles.
    useEffect(() => {
        if (
            !canScrollToTarget ||
            lastScrolledMailIdRef.current === targetMailId
        )
            return;
        lastScrolledMailIdRef.current = targetMailId ?? null;

        const content = contentRef.current;
        if (!content) return;

        const scrollToTarget = () =>
            mailRef.current?.scrollIntoView({ block: "start" });
        scrollToTarget();

        const observer = new ResizeObserver(scrollToTarget);
        observer.observe(content);
        const timeoutId = window.setTimeout(() => observer.disconnect(), 1000);

        return () => {
            observer.disconnect();
            window.clearTimeout(timeoutId);
        };
    }, [canScrollToTarget, targetMailId]);

    useEffect(() => {
        if (mailsThread && mailsThread.mails.length > newMailCount) {
            setNewMailCount(mailsThread.mails.length);
        }
    }, [mailsThread]);

    if (!mail) {
        return null;
    }

    const renderContent = () => {
        // AB#1900 — `?.length` et non la présence du tableau : un tableau VIDE est truthy, donc `.map`
        // ne rendait rien (volet blanc et muet, sans erreur ni log). Un fil vide est légitime — mail
        // dont la seule copie est en corbeille, masqué côté SDK → repli « mail ouvert » ci-dessous.
        if (
            mailsThreadFiltered?.length &&
            !isLoading &&
            userProfile &&
            !isUserProfileLoading
        ) {
            return mailsThreadFiltered.map((mailThread, index) => (
                <MailContentWithSenderAndDate
                    key={mailThread.id}
                    ref={mailThread.id === mail.id ? mailRef : undefined}
                    isSelected={mailThread.id === mail.id}
                    selectedClassName={selectedClassName}
                    isSender={
                        mailThread.from.email === userProfile?.profile.email
                    }
                    mail={mailThread}
                    showFullMessage={index === 0} // First mail is always shown entirely for forwarded mails case
                />
            ));
        }
        if (isLoading || !userProfile || isUserProfileLoading) {
            return <Spinner />;
        }

        return <MailContentWithSenderAndDate mail={mail} />;
    };

    return (
        <ScrollArea className="h-full w-full">
            <div
                ref={contentRef}
                className="flex min-h-full w-full flex-col items-end justify-end gap-8 px-5 py-5 pr-4"
            >
                {renderContent()}
            </div>
        </ScrollArea>
    );
};

export default MailFeed;
